import { Injectable, signal } from '@angular/core';
import { Observable, of, switchMap, tap, catchError, map } from 'rxjs';
import { ThumbnailResource } from '../resource/thumbnail.resource';
import { MapService } from './map.service';
import { MapScreenshotService } from '../../services/map-screenshot.service';
import { PreviewService } from './preview.service';

@Injectable({ providedIn: 'root' })
export class ThumbnailService {
  private isGenerating = signal(false);
  private debounceTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private resource: ThumbnailResource,
    private mapService: MapService,
    private screenshotService: MapScreenshotService,
    private previewService: PreviewService
  ) {}

  /**
   * Mark that a visual change occurred - will trigger thumbnail generation after debounce
   * Also clears the preview cache for this map
   */
  markVisualChange(mapId: string): void {
    if (!mapId) return;

    // Clear preview cache immediately so store browser shows fresh data
    this.previewService.clearCacheForMap(mapId);

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(() => {
      if (!this.isGenerating()) {
        this.generateAndUpload(mapId).subscribe();
      }
    }, 10000);
  }

  /**
   * Trigger thumbnail generation for the current map
   */
  markVisualChangeForCurrentMap(): void {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (currentMap?.id) {
      this.markVisualChange(currentMap.id);
    }
  }

  /**
   * Generate and upload thumbnail immediately (for publish flow)
   * @returns Observable with the S3 key or null if failed
   */
  generateImmediately(mapId: string): Observable<string | null> {
    return this.captureAndUpload(mapId);
  }

  /**
   * Get a presigned URL for viewing a thumbnail
   */
  getPresignedUrl(key: string): Observable<string | null> {
    if (!key) return of(null);
    return this.resource.getPresignedUrl(key).pipe(
      map(response => response.url),
      catchError(() => of(null))
    );
  }

  /**
   * Delete thumbnail from S3
   */
  deleteThumbnail(mapId: string): Observable<void> {
    const key = `smapshots/${mapId}.png`;
    return this.resource.delete(key).pipe(
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  private generateAndUpload(mapId: string): Observable<string | null> {
    if (this.isGenerating()) return of(null);

    this.isGenerating.set(true);
    return this.captureAndUpload(mapId).pipe(
      tap({
        complete: () => this.isGenerating.set(false),
        error: () => this.isGenerating.set(false)
      }),
      catchError(err => {
        console.warn('Thumbnail generation error:', err);
        return of(null);
      })
    );
  }

  private captureAndUpload(mapId: string): Observable<string | null> {
    return new Observable<Blob | null>(subscriber => {
      this.screenshotService.captureMap(2)
        .then(blob => {
          subscriber.next(blob);
          subscriber.complete();
        })
        .catch(err => subscriber.error(err));
    }).pipe(
      switchMap(blob => {
        if (!blob) {
          console.warn('Failed to capture map screenshot for thumbnail');
          return of(null);
        }
        return this.uploadAndSave(mapId, blob);
      }),
      catchError(err => {
        console.warn('Thumbnail capture/upload error:', err);
        return of(null);
      })
    );
  }

  /**
   * Upload thumbnail to S3 and save the URL to the map entity if not already set
   */
  private uploadAndSave(mapId: string, blob: Blob): Observable<string | null> {
    const file = new File([blob], `${mapId}.png`, { type: 'image/png' });
    const thumbnailKey = `smapshots/${mapId}.png`;

    return this.resource.upload(mapId, file).pipe(
      switchMap(() => {
        const currentMap = this.mapService.getCurrentSelectedMapFromStore();

        // Only update database if thumbnailUrl is not set yet
        if (currentMap?.id === mapId && !currentMap.thumbnailUrl) {
          return this.resource.setThumbnailUrl(mapId, thumbnailKey).pipe(
            tap(() => {
              currentMap.thumbnailUrl = thumbnailKey;
              this.mapService.setCurrentSelectedMapToStore(currentMap);
            }),
            map(() => thumbnailKey)
          );
        }

        return of(thumbnailKey);
      }),
      catchError(err => {
        console.warn('Thumbnail upload/save error:', err);
        return of(null);
      })
    );
  }
}
